/* eslint-disable react/no-array-index-key */
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BiLogOut } from "react-icons/bi";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import {
  IMenuConf,
  getSuperadminRootMenu,
  getMonitoringMenu,
  getProMenu,
} from "conf/menu";

import useAuth from "hooks/useAuth";
import useRouter from "common/hooks/use-router";

interface IMenu {
  currentId: string;
  setSidebarOpen?: any;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const menuStyle = "text-primary-50/90 hover:bg-primary-700";

function Menu({
  currentId,
  setSidebarOpen,
  collapsed = false,
  onToggleCollapse,
}: IMenu) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { user: currentUser, signout } = useAuth();

  const renderMenu = (currentMenu: IMenuConf) => {
    return Object.keys(currentMenu).map((key: string, index: number) => {
      const { label, children, ignoreGroup } = currentMenu[key];

      const childrenList = children?.map(({ id, Icon, label: childLabel }) => {
        const isCurrent = currentId?.includes(id);
        return (
          <Link
            key={id}
            to={`/${id}`}
            className={classNames(
              isCurrent ? "bg-primary-700 text-white shadow-sm" : menuStyle,
              "group flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-colors",
              collapsed && "justify-center"
            )}
            onClick={setSidebarOpen ? () => setSidebarOpen(false) : () => {}}
          >
            <div
              className={classNames(
                isCurrent
                  ? "bg-primary-600 text-white"
                  : "text-primary-100/80 group-hover:text-white",
                collapsed ? "" : "mr-3",
                collapsed && "mx-auto",
                "flex-shrink-0 h-7 w-7 flex items-center justify-center rounded-md"
              )}
            >
              <Icon
                className={classNames("flex-shrink-0 h-4 w-4")}
                aria-hidden="true"
              />
            </div>
            {!collapsed && t(`titlePages.${childLabel}`)}
          </Link>
        );
      });
      return ignoreGroup || !childrenList?.length ? (
        <div
          className="mt-4 mb-2 text-xs text-primary-100/70 font-semibold"
          key={`menu-${index}`}
        >
          {childrenList}
        </div>
      ) : (
        <div key={`menu-group${index}`}>
          {collapsed ? (
            <div
              className="flex justify-center mt-4 mb-2"
              key={`menu-${index}`}
            >
              <div className="w-1 h-1 bg-primary-100/40 rounded-full" />
            </div>
          ) : (
            <div
              className="mt-4 mb-2 text-xs text-primary-100/70 font-semibold"
              key={`menu-${index}`}
            >
              {t(`titleDividers.${label}`)}
            </div>
          )}
          {childrenList}
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col flex-1 h-full bg-primary-800 border-r-2 border-primary-700">
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-primary-700">
        {!collapsed && (
          <img
            src="/images/logo-text.png"
            className="object-contain h-7"
            alt="Logo"
          />
        )}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-primary-100 hover:text-white hover:bg-primary-700 rounded transition-colors"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRightIcon className="w-4 h-4" />
            ) : (
              <ChevronLeftIcon className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      <div
        className="overflow-y-auto flex-1 px-4 pb-6
        scrollbar-thin scrollbar-thumb-primary-600 scrollbar-track-primary-800 scrollbar-thumb-rounded scrollbar-track-rounded"
      >
        {renderMenu(
          currentUser?.isMonitoring
            ? getMonitoringMenu()
            : currentUser?.isAdmin || currentUser?.isSuperadmin
            ? getSuperadminRootMenu()
            : getProMenu()
        )}

        <button
          type="button"
          className={classNames(
            "opacity-80 hover:opacity-100 group flex items-center px-2 py-1.5 text-sm font-medium rounded-md text-primary-100/90 hover:bg-primary-700 w-full transition-colors",
            collapsed && "justify-center px-2"
          )}
          onClick={() => {
            signout();
            navigate("/login");
          }}
        >
          <div
            className={classNames(
              collapsed ? "" : "mr-3",
              collapsed && "mx-auto",
              "flex-shrink-0 h-7 w-7 flex items-center justify-center rounded-md"
            )}
          >
            <BiLogOut
              className={classNames(
                "text-primary-100/80 group-hover:text-white",
                "flex-shrink-0 h-4 w-4"
              )}
              aria-hidden="true"
            />
          </div>
          {!collapsed && t("titlePages.logout")}
        </button>
      </div>
    </div>
  );
}

export default Menu;
